<div class="garage-header row mx-0 gy-2">
  <h1 class="col-12 col-sm-auto noselect">My Garage</h1>
  <!-- *ngIf on the column, not the button: an empty col-12 still contributes
       gutter-y, leaving dead space under the title on the empty-garage screen. -->
  <div *ngIf="vehicles.length > 0" class="col-12 col-sm-auto ms-sm-auto">
    <button class="btn btn-primary btn-add-vehicle" (click)="openAddVehicleModal()" [disabled]="loading">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 5V15M5 10H15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      Add Vehicle
    </button>
  </div>
</div>

<!-- Error Message -->
<div *ngIf="error" class="error-message">
  {{ error }}
  <button (click)="clearError()" class="close-error">&times;</button>
</div>

<!-- Filters (kept mounted with an active customer filter so it can be cleared
     even with zero results). No `!loading` in the guard: loadVehicles sets
     loading without clearing `vehicles`, so gating on it would tear the panel
     down on every change and drop focus out of the select just used. -->
<div *ngIf="vehicles.length > 0 || custUserId" class="filters">
  <button
    type="button"
    class="filters-toggle noselect"
    [class.expanded]="!filtersCollapsed"
    aria-controls="garageFilters"
    [attr.aria-expanded]="!filtersCollapsed"
    (click)="filtersCollapsed = !filtersCollapsed">
    <span class="filters-title">Filters &amp; Sorting</span>
    <!-- Hidden on xs, where it would crowd out the title. -->
    <span *ngIf="filtersCollapsed" class="filters-summary d-none d-sm-block">{{ filterSummary }}</span>
    <span class="filters-toggle-end">
      <span *ngIf="activeFilterCount > 0" class="filters-count">{{ activeFilterCount }}</span>
      <span class="fa-solid" [ngClass]="filtersCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></span>
    </span>
  </button>

  <!-- ngbCollapse owns `display` on this element, so it must not be the grid:
       `.collapse:not(.show)` and a component-scoped `.filter-grid[_ngcontent]`
       are both specificity (0,2,0), and Angular appends component styles after
       styles.css, so `display: grid` would win and the panel would never hide.
       One-way binding — ngbCollapseChange only fires from NgbCollapse.toggle(). -->
  <div id="garageFilters" class="filters-panel" [ngbCollapse]="filtersCollapsed">
    <div class="filter-grid">
      <div *ngIf="hasViewAllGarages" class="sort-group noselect">
        <label for="customerFilter">Customer</label>
        <select
          id="customerFilter"
          class="form-select noselect"
          [(ngModel)]="custUserId"
          (ngModelChange)="onCustomerFilterChange()">
          <option value="">All Customers</option>
          <option *ngFor="let customer of customers" [value]="customer.userId">
            {{ getCustomerDisplayName(customer) }}
          </option>
        </select>
      </div>
      <div class="sort-group noselect">
        <label for="sortField">Sort by</label>
        <select
          id="sortField"
          class="form-select noselect"
          [(ngModel)]="sortField"
          (ngModelChange)="onSortFieldChange($event)">
          <option *ngFor="let option of sortOptions" [value]="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div class="sort-group noselect">
        <label for="sortDirection">Order</label>
        <select
          id="sortDirection"
          class="form-select noselect"
          [(ngModel)]="sortDirection"
          (ngModelChange)="onSortDirectionChange($event)">
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>
      <div class="sort-group noselect">
        <label class="noselect" for="pageSize">Page Size</label>
        <!-- [ngValue], not [value]: NgSelectOption's [value] setter never
             registers in the value accessor's option map, so the model reads
             back the string "10" instead of the number 10. -->
        <select
          id="pageSize"
          class="form-select noselect"
          [(ngModel)]="pageSize"
          (ngModelChange)="onPageSizeChange($event)">
          <option [ngValue]="5">5</option>
          <option [ngValue]="10">10</option>
          <option [ngValue]="20">20</option>
          <option [ngValue]="50">50</option>
        </select>
      </div>
    </div>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="loading" class="loading-container">
  <div class="spinner"></div>
  <p>Loading vehicles...</p>
</div>

<!-- Empty State -->
<div *ngIf="!loading && vehicles.length === 0 && !custUserId" class="empty-state">
  <h2>No Vehicles Yet</h2>
  <p>Add your first vehicle to get started</p>
  <button class="btn-primary" (click)="openAddVehicleModal()">Add Your First Vehicle</button>
</div>

<!-- Empty State (customer filter active) -->
<div *ngIf="!loading && vehicles.length === 0 && custUserId" class="empty-state">
  <h2>No Vehicles Found</h2>
  <p>This customer has no vehicles in their garage</p>
  <button class="btn-primary" (click)="openAddVehicleModal()">Add Vehicle</button>
</div>

<div class="grid-wrap">
  <!-- Vehicles Grid -->
  <div *ngIf="!loading && vehicles.length > 0" class="vehicles-grid">
    <a [routerLink]="['/my-garage', vehicle.VehicleId]" *ngFor="let vehicle of vehicles" class="vehicle-card">
      <!-- Image Carousel/Gallery -->
      <div class="vehicle-images">
        <div class="image-container">
          <img 
            *ngIf="vehicle.VehicleImages && vehicle.VehicleImages.length > 0; else noImage"
            [src]="vehicle.VehicleImages[0].ImageUrl" 
            [alt]="getVehicleDisplayName(vehicle)"
            class="vehicle-image"
          />
          <ng-template #noImage>
            <!-- "no-image" instead of "placeholder": Bootstrap's .placeholder sets cursor: wait -->
            <img [src]="getDefaultImage()" alt="No image" class="vehicle-image no-image" />
          </ng-template>
        </div>
      </div>

      <div class="vehicle-details noselect">
        <h3>{{ getVehicleDisplayName(vehicle) }}</h3>
        <div class="vehicle-info">
          <div *ngIf="vehicle.Vin" class="info-item">
            <span class="info-label">VIN:</span>
            <span class="info-value">{{ vehicle.Vin }}</span>
          </div>
          <!-- The blank-row placeholder that used to live here is gone: the card
               is a flex column and .card-actions has margin-top: auto, so the
               button aligns without faking a row. -->
          <div *ngIf="vehicle.LicensePlate" class="info-item">
            <span class="info-label">Plate:</span>
            <span class="info-value">{{ vehicle.LicensePlate }}</span>
          </div>
          <div *ngIf="permissions?.includes('VIEW_ALL_GARAGES')" class="info-item">
            <span class="info-label">Customer Name:</span>
            <span class="info-value">{{ vehicle.CustFirstName + ' ' + vehicle.CustLastName }}</span>
          </div>
          <div *ngIf="permissions?.includes('VIEW_ALL_GARAGES')" class="info-item">
            <span class="info-label">Customer Email:</span>
            <span class="info-value">{{ vehicle.CustEmail }}</span>
          </div>
        </div>
      </div>

      <div class="card-actions">
        <div class="col btn btn-primary">View Details</div>
      </div>
    </a>
  </div>
</div>

<!-- Pagination -->
<app-pagination
  *ngIf="!loading && vehicles.length > 0 && totalPages >= 2"
  [currentPage]="pageNumber"
  [totalPages]="totalPages"
  (pageChange)="onPageChange($event)">
</app-pagination>

<!-- Upload Preview Modal -->
<div *ngIf="imagePreview" class="upload-modal">
  <div class="modal-content">
    <h3>Upload Image</h3>
    <img [src]="imagePreview" alt="Preview" class="preview-image" />
    <div *ngIf="uploadError" class="upload-error">{{ uploadError }}</div>
    <div class="modal-actions">
      <button (click)="clearImageSelection()" [disabled]="uploadingImage" class="btn-secondary">Cancel</button>
      <button *ngIf="uploadingImage" disabled class="btn-primary">
        <span class="spinner-small"></span> Uploading...
      </button>
    </div>
  </div>
  <div class="modal-backdrop" (click)="clearImageSelection()"></div>
</div>